import { jsPDF } from 'jspdf';

// =============================================================================
// Thinkific Progress Report - PDF generator
// -----------------------------------------------------------------------------
// Builds a paginated, multi-student PDF matching the data Thinkific shows on
// the Enrollments, Activity feed, and Quiz & Survey tabs of their admin UI.
//
// Each student starts on a fresh page. Long tables wrap across pages via the
// addPageIfNeeded() helper, so we never split a row in half.
//
// Public API:
//   generateThinkificReportPdf(reports, { reportTitle, generatedFor })
//     reports       : Array of report payloads from /thinkific-report
//     reportTitle   : Optional override for the cover/title heading
//     generatedFor  : Optional subtitle (e.g. cohort name or "5 students")
// =============================================================================

const MARGIN = 18;
const FONT_BODY = 9.5;
const FONT_SMALL = 8.5;
const FONT_H1 = 18;
const FONT_H2 = 13;
const FONT_H3 = 11;
const ROW_PAD_Y = 2.2;

// ---------------------------------------------------------------------------
// Formatting helpers - mirror the conventions used by ThinkificEnrollment.jsx
// so the PDF reads the same way as the admin UI.
// ---------------------------------------------------------------------------
function fmtDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fmtPercent(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const pct = n > 1 ? n : n * 100;
  return `${Math.round(pct)}%`;
}

function fmtNumber(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return String(n);
}

// Map our internal contentable types to the same short labels Thinkific
// uses in the activity feed icon column.
function fmtContentType(t) {
  if (!t) return 'Lesson';
  switch (String(t).toLowerCase()) {
    case 'lesson':
    case 'video':
    case 'multimedia':
      return 'Video';
    case 'text':
      return 'Text';
    case 'pdf':
    case 'download':
      return 'PDF';
    case 'audio':
      return 'Audio';
    case 'assignment':
      return 'Assignment';
    case 'exam':
      return 'Exam';
    case 'quiz':
      return 'Quiz';
    case 'survey':
      return 'Survey';
    default:
      return String(t);
  }
}

// ---------------------------------------------------------------------------
// Drawing primitives. Everything funnels through addPageIfNeeded() so we
// can guarantee clean page breaks - no half-rendered rows across pages.
// ---------------------------------------------------------------------------
function makeContext(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  return {
    doc,
    pageWidth,
    pageHeight,
    contentWidth: pageWidth - MARGIN * 2,
    y: MARGIN,
  };
}

function addPageIfNeeded(ctx, required) {
  if (ctx.y + required > ctx.pageHeight - MARGIN) {
    ctx.doc.addPage();
    ctx.y = MARGIN;
    return true;
  }
  return false;
}

function drawText(ctx, text, { x = MARGIN, size = FONT_BODY, bold = false, color = 0 } = {}) {
  ctx.doc.setFontSize(size);
  ctx.doc.setFont('helvetica', bold ? 'bold' : 'normal');
  if (Array.isArray(color)) ctx.doc.setTextColor(...color);
  else ctx.doc.setTextColor(color);
  ctx.doc.text(text, x, ctx.y);
  ctx.doc.setTextColor(0);
}

function drawHr(ctx) {
  ctx.doc.setDrawColor(220);
  ctx.doc.line(MARGIN, ctx.y, ctx.pageWidth - MARGIN, ctx.y);
}

// ---------------------------------------------------------------------------
// Table renderer.
//
// columns: [{ header, width (mm), align?: 'left'|'right', getCell(row) -> string }]
// rows   : array of row objects passed to getCell
//
// - Header is repeated at the top of every new page when the table wraps.
// - Cells wrap inside their column width.
// - Row height = max wrapped-line count * line height + padding.
// ---------------------------------------------------------------------------
function drawTable(ctx, { columns, rows, emptyMessage }) {
  const lineHeight = 4.2;
  const headerHeight = 6.5;

  // Normalize column widths so they fit ctx.contentWidth exactly.
  const totalWidth = columns.reduce((sum, c) => sum + c.width, 0);
  const scale = ctx.contentWidth / totalWidth;
  const cols = columns.map((c) => ({ ...c, width: c.width * scale }));

  const drawHeader = () => {
    ctx.doc.setFillColor(243, 244, 246);
    ctx.doc.rect(MARGIN, ctx.y, ctx.contentWidth, headerHeight, 'F');
    ctx.doc.setFontSize(FONT_SMALL);
    ctx.doc.setFont('helvetica', 'bold');
    ctx.doc.setTextColor(55);
    let x = MARGIN;
    for (const col of cols) {
      ctx.doc.text(col.header, x + 2, ctx.y + 4.5);
      x += col.width;
    }
    ctx.doc.setTextColor(0);
    ctx.y += headerHeight;
  };

  addPageIfNeeded(ctx, headerHeight + lineHeight * 2);
  drawHeader();

  if (!rows || rows.length === 0) {
    ctx.doc.setFontSize(FONT_BODY);
    ctx.doc.setFont('helvetica', 'italic');
    ctx.doc.setTextColor(140);
    ctx.doc.text(emptyMessage || 'No data.', MARGIN + 2, ctx.y + lineHeight);
    ctx.doc.setTextColor(0);
    ctx.doc.setFont('helvetica', 'normal');
    ctx.y += lineHeight + 6;
    return;
  }

  ctx.doc.setFontSize(FONT_SMALL);
  ctx.doc.setFont('helvetica', 'normal');

  rows.forEach((row, idx) => {
    // Pre-wrap each cell to compute the tallest cell in this row.
    const wrapped = cols.map((col) => {
      const raw = col.getCell(row) ?? '';
      const text = String(raw);
      return ctx.doc.splitTextToSize(text, col.width - 4);
    });
    const lineCount = Math.max(1, ...wrapped.map((w) => w.length));
    const rowHeight = lineCount * lineHeight + ROW_PAD_Y * 2;

    if (addPageIfNeeded(ctx, rowHeight)) {
      drawHeader();
      ctx.doc.setFontSize(FONT_SMALL);
      ctx.doc.setFont('helvetica', 'normal');
    }

    // Zebra-stripe for readability on dense tables.
    if (idx % 2 === 1) {
      ctx.doc.setFillColor(250, 250, 250);
      ctx.doc.rect(MARGIN, ctx.y, ctx.contentWidth, rowHeight, 'F');
    }

    let x = MARGIN;
    for (let i = 0; i < cols.length; i++) {
      const col = cols[i];
      const lines = wrapped[i];
      const align = col.align || 'left';
      const textX = align === 'right' ? x + col.width - 2 : x + 2;
      ctx.doc.text(lines, textX, ctx.y + ROW_PAD_Y + lineHeight - 1, { align });
      x += col.width;
    }
    ctx.y += rowHeight;
  });

  // Bottom border on the table, plus breathing room before the next
  // section heading so each block reads as its own group.
  ctx.doc.setDrawColor(220);
  ctx.doc.line(MARGIN, ctx.y, ctx.pageWidth - MARGIN, ctx.y);
  ctx.y += 6;
}

// ---------------------------------------------------------------------------
// Section heading + per-student page.
// ---------------------------------------------------------------------------
function drawSectionHeading(ctx, title) {
  addPageIfNeeded(ctx, 18);
  // Top margin so each section reads as a distinct block instead of
  // butting against the table above.
  ctx.y += 6;
  drawText(ctx, title, { size: FONT_H3, bold: true });
  ctx.y += 5;
  drawHr(ctx);
  ctx.y += 4;
}

function drawStudentPage(ctx, report) {
  const { subject, enrollment, activity, quizzes, certificate, warnings, diagnostics } = report;

  // Student banner.
  drawText(ctx, subject.displayName || subject.email || 'Student', {
    size: FONT_H2,
    bold: true,
  });
  ctx.y += 6;

  ctx.doc.setFontSize(FONT_SMALL);
  ctx.doc.setFont('helvetica', 'normal');
  ctx.doc.setTextColor(100);
  const metaLine = [subject.email, subject.cohortName].filter(Boolean).join('  •  ');
  if (metaLine) {
    ctx.doc.text(metaLine, MARGIN, ctx.y);
    ctx.y += 5;
  }
  ctx.doc.setTextColor(0);

  drawHr(ctx);
  ctx.y += 5;

  if (warnings && warnings.length > 0) {
    ctx.doc.setFontSize(FONT_SMALL);
    ctx.doc.setFont('helvetica', 'italic');
    ctx.doc.setTextColor(180, 90, 0);
    for (const w of warnings) {
      addPageIfNeeded(ctx, 5);
      const lines = ctx.doc.splitTextToSize(`Note: ${w}`, ctx.contentWidth);
      ctx.doc.text(lines, MARGIN, ctx.y);
      ctx.y += lines.length * 4 + 2;
    }
    ctx.doc.setTextColor(0);
    ctx.doc.setFont('helvetica', 'normal');
    ctx.y += 2;
  }

  // ---- Enrollment ------------------------------------------------------
  drawSectionHeading(ctx, 'Enrollments');
  drawTable(ctx, {
    columns: [
      { header: 'Course', width: 30, getCell: (e) => e.courseName || '—' },
      { header: 'Progress', width: 12, getCell: (e) => fmtPercent(e.percentageCompleted) },
      { header: 'Enrollment Status', width: 16, getCell: (e) => e.status || '—' },
      {
        header: 'Certificate Issued',
        width: 18,
        getCell: () =>
          certificate?.issued
            ? `Yes${certificate.issuedAt ? ` (${fmtDate(certificate.issuedAt)})` : ''}`
            : 'No',
      },
      { header: 'Expiry Date', width: 18, getCell: (e) => fmtDate(e.expiryDate) },
    ],
    rows: enrollment ? [enrollment] : [],
    emptyMessage: 'No enrollment found for this student in the NCLEX Review Course.',
  });

  // ---- Activity feed ---------------------------------------------------
  drawSectionHeading(ctx, `Activity feed${activity.length ? `  (${activity.length})` : ''}`);
  drawTable(ctx, {
    columns: [
      { header: 'Product', width: 22, getCell: () => enrollment?.courseName || 'NCLEX Review Course' },
      { header: 'Type', width: 10, getCell: (a) => fmtContentType(a.type) },
      { header: 'Content', width: 38, getCell: (a) => a.name || '—' },
      { header: 'Completed on', width: 24, getCell: (a) => fmtDateTime(a.completedAt) },
    ],
    rows: activity,
    emptyMessage: 'No completed content yet.',
  });

  // ---- Quiz & Survey ---------------------------------------------------
  drawSectionHeading(ctx, `Quiz & Survey${quizzes.length ? `  (${quizzes.length})` : ''}`);
  drawTable(ctx, {
    columns: [
      { header: 'Course', width: 22, getCell: () => enrollment?.courseName || 'NCLEX Review Course' },
      { header: 'Type', width: 9, getCell: (q) => q.type || 'Quiz' },
      { header: 'Quiz / Survey', width: 25, getCell: (q) => q.name || '—' },
      { header: 'Completed', width: 18, getCell: (q) => fmtDate(q.completedAt) },
      { header: 'Pass %', width: 9, align: 'right', getCell: (q) => fmtPercent(q.passingGrade) },
      { header: 'Grade', width: 9, align: 'right', getCell: (q) => fmtPercent(q.studentGrade) },
      { header: 'Questions', width: 10, align: 'right', getCell: (q) => fmtNumber(q.totalQuestions) },
      { header: 'Correct', width: 10, align: 'right', getCell: (q) => fmtNumber(q.totalCorrect) },
    ],
    rows: quizzes,
    emptyMessage: 'No quiz or survey attempts yet.',
  });

  // ---- Diagnostics (only present when diagnose=true on the request) ----
  if (Array.isArray(diagnostics) && diagnostics.length > 0) {
    ctx.doc.addPage();
    ctx.y = MARGIN;

    drawText(ctx, 'Diagnostics — raw Thinkific responses', { size: FONT_H2, bold: true });
    ctx.y += 6;
    drawText(ctx, 'Use this to identify which endpoint returns lesson/quiz data on this Thinkific plan.', { size: FONT_SMALL, color: [100, 100, 100] });
    ctx.y += 6;
    drawHr(ctx);
    ctx.y += 5;

    for (const probe of diagnostics) {
      addPageIfNeeded(ctx, 30);
      drawText(ctx, probe.label || probe.path, { size: FONT_H3, bold: true });
      ctx.y += 5;

      ctx.doc.setFontSize(FONT_SMALL);
      ctx.doc.setFont('helvetica', 'normal');
      ctx.doc.setTextColor(80);
      const meta = [
        `path: ${probe.path}`,
        `status: ${probe.status}`,
        `ok: ${probe.ok}`,
        probe.count != null ? `items: ${probe.count}` : null,
      ].filter(Boolean).join('   ');
      const metaLines = ctx.doc.splitTextToSize(meta, ctx.contentWidth);
      ctx.doc.text(metaLines, MARGIN, ctx.y);
      ctx.y += metaLines.length * 4 + 2;
      ctx.doc.setTextColor(0);

      // Monospace-ish sample using courier.
      ctx.doc.setFont('courier', 'normal');
      ctx.doc.setFontSize(7.5);
      const sampleLines = ctx.doc.splitTextToSize(probe.sample || '(no body)', ctx.contentWidth);
      for (const line of sampleLines) {
        addPageIfNeeded(ctx, 3.5);
        ctx.doc.text(line, MARGIN, ctx.y);
        ctx.y += 3.2;
      }
      ctx.doc.setFont('helvetica', 'normal');
      ctx.y += 4;
    }
  }
}

// ---------------------------------------------------------------------------
// Cover page for multi-student reports. Lists the students included so the
// reader can confirm the report scope before flipping through the detail.
// ---------------------------------------------------------------------------
function drawCoverPage(ctx, reports, { reportTitle, generatedFor }) {
  drawText(ctx, reportTitle || 'Thinkific Progress Report', {
    size: FONT_H1,
    bold: true,
  });
  ctx.y += 9;

  drawText(ctx, 'NCLEX Review Course', { size: FONT_H3, color: [80, 80, 80] });
  ctx.y += 6;

  ctx.doc.setFontSize(FONT_SMALL);
  ctx.doc.setFont('helvetica', 'normal');
  ctx.doc.setTextColor(110);
  const lines = [
    `Generated ${new Date().toLocaleString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    })}`,
  ];
  if (generatedFor) lines.push(generatedFor);
  lines.push(`${reports.length} student${reports.length === 1 ? '' : 's'}`);
  for (const line of lines) {
    ctx.doc.text(line, MARGIN, ctx.y);
    ctx.y += 4.5;
  }
  ctx.doc.setTextColor(0);
  ctx.y += 6;

  drawHr(ctx);
  ctx.y += 6;

  drawText(ctx, 'Students included', { size: FONT_H3, bold: true });
  ctx.y += 5;

  drawTable(ctx, {
    columns: [
      { header: '#', width: 6, align: 'right', getCell: (r) => String((r._idx ?? 0) + 1) },
      { header: 'Name', width: 30, getCell: (r) => r.subject.displayName || '—' },
      { header: 'Email', width: 30, getCell: (r) => r.subject.email || '—' },
      { header: 'Cohort', width: 18, getCell: (r) => r.subject.cohortName || '—' },
      {
        header: 'Progress',
        width: 10,
        align: 'right',
        getCell: (r) => fmtPercent(r.enrollment?.percentageCompleted),
      },
      {
        header: 'Status',
        width: 12,
        getCell: (r) => r.enrollment?.status || 'Not enrolled',
      },
    ],
    rows: reports.map((r, i) => ({ ...r, _idx: i })),
    emptyMessage: 'No students.',
  });
}

// ---------------------------------------------------------------------------
// Public entry point.
// ---------------------------------------------------------------------------
export function generateThinkificReportPdf(reports, options = {}) {
  if (!Array.isArray(reports) || reports.length === 0) {
    throw new Error('generateThinkificReportPdf: no reports to render.');
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const ctx = makeContext(doc);

  // For a single-student report, skip the cover page - the student banner
  // on page 1 is already a clear title. Group reports get a cover.
  if (reports.length > 1) {
    drawCoverPage(ctx, reports, options);
  }

  reports.forEach((report, i) => {
    if (i > 0 || reports.length > 1) {
      doc.addPage();
      ctx.y = MARGIN;
    }
    drawStudentPage(ctx, report);
  });

  // Footer page numbers on every page.
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(
      `Page ${p} of ${pageCount}`,
      doc.internal.pageSize.getWidth() - MARGIN,
      doc.internal.pageSize.getHeight() - 8,
      { align: 'right' }
    );
    doc.setTextColor(0);
  }

  // Filename: single student -> name; group -> count + date.
  const stamp = new Date().toISOString().slice(0, 10);
  const safe = (s) => String(s || '').replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '');
  const filename =
    reports.length === 1
      ? `Thinkific_Progress_${safe(reports[0].subject.displayName)}_${stamp}.pdf`
      : `Thinkific_Progress_${reports.length}_students_${stamp}.pdf`;

  doc.save(filename);
}
